import React, { useMemo, useState } from 'react';
import {
  Handle,
  NodeProps,
  Position,
} from 'reactflow';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch } from '../../../../support/store';
import { useActiveJourneyMapDraft } from '../../../../hooks/journey-map-hooks';
import {
  addJourneyMapDraftItemAfter,
  addJourneyMapDraftLinkBetweenItems,
  selectJourneyMapDraftItem,
  selectJourneyMapDraftLink,
} from '../../../../support/slices/journeyMapBuilderSlice';
import { JourneyNodeData, LinkTypeOption } from './journeyMapCanvasTypes';
import JourneyMapCard from '../../../Common/JourneyMap/JourneyMapCard';
import {
  JOURNEY_NODE_HEIGHT,
  JOURNEY_NODE_WIDTH,
} from '../../../../support/journeyMap/constants';

type HandleConfig = {
  id: string;
  type: 'source' | 'target';
  position: Position;
  style: React.CSSProperties;
};

const buildHandles = (): HandleConfig[] => {
  return [
    {
      id: 'target-top',
      type: 'target',
      position: Position.Top,
      style: {
        top: -10,
        left: '50%',
        transform: 'translate(-50%, 0)',
      },
    },
    {
      id: 'source-top',
      type: 'source',
      position: Position.Top,
      style: {
        top: 0,
        left: '50%',
        transform: 'translate(-50%, 0)',
      },
    },

    {
      id: 'target-right',
      type: 'target',
      position: Position.Right,
      style: {
        right: -10,
        top: '50%',
        transform: 'translate(0, -50%)',
      },
    },
    {
      id: 'source-right',
      type: 'source',
      position: Position.Right,
      style: {
        right: 0,
        top: '50%',
        transform: 'translate(0, -50%)',
      },
    },

    {
      id: 'target-bottom',
      type: 'target',
      position: Position.Bottom,
      style: {
        bottom: -10,
        left: '50%',
        transform: 'translate(-50%, 0)',
      },
    },
    {
      id: 'source-bottom',
      type: 'source',
      position: Position.Bottom,
      style: {
        bottom: 0,
        left: '50%',
        transform: 'translate(-50%, 0)',
      },
    },

    {
      id: 'target-left',
      type: 'target',
      position: Position.Left,
      style: {
        left: -10,
        top: '50%',
        transform: 'translate(0, -50%)',
      },
    },
    {
      id: 'source-left',
      type: 'source',
      position: Position.Left,
      style: {
        left: 0,
        top: '50%',
        transform: 'translate(0, -50%)',
      },
    },
  ];
};

const HANDLES = buildHandles();

function JourneyMapCanvasNodeComponent({ data }: NodeProps<JourneyNodeData>) {
  const dispatch = useAppDispatch();
  const draft = useActiveJourneyMapDraft();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [targetTempId, setTargetTempId] = useState('');
  const [linkType, setLinkType] = useState<LinkTypeOption>('default');

  const availableTargets = useMemo(() => {
    return (draft?.items || []).filter(
      (item) => !item.isDeleted && item.tempId !== data.itemTempId,
    );
  }, [draft?.items, data.itemTempId]);

  const item = useMemo(() => {
    return {
      id: data.itemTempId,
      name: data.title,
      description: data.referenceLabel || '',
      provider: data.provider || '',
      item_type: data.itemType || 'content',
      is_required: true,
      // Mirror the fields the shared card reads for the file-type tag + friendly label.
      metadata: data.displayLabel ? { label: data.displayLabel } : undefined,
      provider_config: data.fileRenderKind || data.fileName
        ? { file: { render_kind: data.fileRenderKind || '', file_name: data.fileName || '' } }
        : undefined,
    };
  }, [data]);

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(selectJourneyMapDraftItem(data.itemTempId));
    dispatch(selectJourneyMapDraftLink(null));
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    dispatch(selectJourneyMapDraftItem(data.itemTempId));
    dispatch(selectJourneyMapDraftLink(null));
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleAdd = (kind: 'content' | 'section', nextLinkType: LinkTypeOption) => {
    dispatch(
      addJourneyMapDraftItemAfter({
        sourceItemTempId: data.itemTempId,
        kind,
        linkType: nextLinkType,
      }),
    );
    handleMenuClose();
  };

  const handleOpenConnectDialog = () => {
    handleMenuClose();
    setTargetTempId('');
    setLinkType('default');
    setConnectDialogOpen(true);
  };

  const handleConfirmConnect = () => {
    if (!targetTempId) return;

    dispatch(
      addJourneyMapDraftLinkBetweenItems({
        fromItemTempId: data.itemTempId,
        toItemTempId: targetTempId,
        linkType,
        sourceHandle: null,
        targetHandle: null,
      }),
    );

    setConnectDialogOpen(false);
  };

  return (
    <>
      {HANDLES.map((handle) => (
        <Handle
          key={handle.id}
          id={handle.id}
          type={handle.type}
          position={handle.position}
          className="journey-map-canvas__handle"
          style={{
            width: 20,
            height: 20,
            background: 'transparent',
            border: 'none',
            opacity: 0,
            pointerEvents: 'auto',
            ...handle.style,
          }}
        />
      ))}

      <Box
        onClick={handleSelect}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          width: `${JOURNEY_NODE_WIDTH}px`,
          minWidth: `${JOURNEY_NODE_WIDTH}px`,
          maxWidth: `${JOURNEY_NODE_WIDTH}px`,
          height: `${JOURNEY_NODE_HEIGHT}px`,
          minHeight: `${JOURNEY_NODE_HEIGHT}px`,
          maxHeight: `${JOURNEY_NODE_HEIGHT}px`,
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -14,
            right: -14,
            zIndex: 20,
          }}
        >
          <Tooltip title="Add next">
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #d0d4d9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                '&:hover': {
                  backgroundColor: '#f7f9fc',
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: '100%',
            minWidth: 0,
            minHeight: 0,
            maxWidth: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
            borderRadius: '8px',
          }}
        >
          <JourneyMapCard
            item={item}
            active={data.selected}
            completed={false}
            locked={false}
            overdue={false}
            showStatusTag={false}
            showTypeTag={true}
            typeTagInline={true}
            onClick={() => {
              dispatch(selectJourneyMapDraftItem(data.itemTempId));
              dispatch(selectJourneyMapDraftLink(null));
            }}
          />
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleAdd('content', 'default')}>Add next item</MenuItem>
        <MenuItem onClick={() => handleAdd('section', 'default')}>Add section</MenuItem>
        <MenuItem onClick={() => handleAdd('content', 'alternate')}>Add alternate path</MenuItem>
        <MenuItem onClick={() => handleAdd('content', 'conditional')}>Add conditional path</MenuItem>
        <MenuItem onClick={() => handleAdd('content', 'test_out')}>Add test-out path</MenuItem>
        <MenuItem onClick={handleOpenConnectDialog}>Connect to existing item</MenuItem>
      </Menu>

      <Dialog
        open={connectDialogOpen}
        onClose={() => setConnectDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Connect to Existing Item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel id="existing-target-label">Target Item</InputLabel>
              <Select
                labelId="existing-target-label"
                label="Target Item"
                value={targetTempId}
                onChange={(e) => setTargetTempId(String(e.target.value))}
              >
                {availableTargets.map((item) => (
                  <MenuItem key={item.tempId} value={item.tempId}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="existing-link-type-label">Link Type</InputLabel>
              <Select
                labelId="existing-link-type-label"
                label="Link Type"
                value={linkType}
                onChange={(e) => setLinkType(e.target.value as LinkTypeOption)}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="alternate">Alternate Successful Path</MenuItem>
                <MenuItem value="conditional">Conditional Path</MenuItem>
                <MenuItem value="test_out">Test-Out Path</MenuItem>
                <MenuItem value="optional">Optional</MenuItem>
                <MenuItem value="fallback">Fallback</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmConnect} disabled={!targetTempId}>
            Connect
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default React.memo(JourneyMapCanvasNodeComponent);